// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import qs from 'query-string';
import React, { useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalFooter } from '@heroui/react';
import { Button } from '@/components/ui/button';
import { type ModalProps, useModal } from '@/stores/use-modal-store.ts';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '@/config';
import { useServerStore } from '@/stores/use-server-store.ts';
import { useSocket } from '@/context/socket-context/use-external-socket.ts';

export const DeleteChannelModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  data,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { channel } = data;
  const [isLoading, setIsLoading] = useState(false);
  const { server } = useServerStore();
  const socket = useSocket();

  const onClick = async () => {
    try {
      setIsLoading(true);
      const url = qs.stringifyUrl({
        url: `${API_BASE}/api/channels/${channel?.id}`,
        query: {
          serverId: server?.id,
        },
      });

      await fetch(url, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(
          JSON.stringify({
            type: 'channel-delete',
            payload: { id: channel?.id },
          })
        );
      }

      onClose();
      navigate(`/servers/${server?.id}`);
    } catch (error) {
      toast.error(`Something went wrong ${error}`);
    } finally {
      setIsLoading(false);
    }
  };
  const handleClose = () => {
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={handleClose}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="text-center text-2xl font-bold">
                {t('DeleteChannelModal.title')}
              </div>
              <div className="text-center">
                {t('DeleteChannelModal.description')} <br />
                <span className="font-semibold text-indigo-500">
                  #{channel?.name}
                </span>{' '}
                {t('DeleteChannelModal.permanentDelete')}
              </div>
            </ModalHeader>
            <ModalFooter>
              <Button disabled={isLoading} onClick={onClose} variant="ghost">
                {t('DeleteChannelModal.cancel')}
              </Button>
              <Button disabled={isLoading} color="danger" onClick={onClick}>
                {t('DeleteChannelModal.confirm')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
